import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { DataService } from 'src/app/_helpers';
import { Configuration } from 'src/app/_models';
import { ConfigurationService } from 'src/app/_services';
import { bluePlusIcon, mailIcon } from 'src/app/sharedcomp/constans/icons.constans';

@Component({
  selector: 'app-edit-notification-form',
  templateUrl: './edit-notification-form.component.html',
  styleUrls: [
    './edit-notification-form.component.css',
    '../../../../../../../assets/css/new-component-view.css',
    '../../../../../../../assets/css/new-buttons-theme.css',
    '../../../../../../../assets/css/new-form-elements.css'
  ]
})
export class EditNotificationFormComponent implements OnInit {

  @Output() cancelEdition: EventEmitter<boolean> = new EventEmitter();
  @Output() getNotificationList: EventEmitter<boolean> = new EventEmitter();
  @Input() customerId: string;
  @Input() custNotifyTemplateList: any[] = [];
  @Input() notificationPublicId: string;

  icons: Record<string, { path: string; alt: string }>;

  editNotificationForm: FormGroup;
  templateListLimit: number = 100;
  isLimitInEdit: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private configurationService: ConfigurationService,
    private dataService: DataService
  ) {
    this.icons = this.setIcons();
    this.initNewNotificationForm();
  }

  ngOnInit() {
    this.getNotificationDetail(this.notificationPublicId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.customerId) {
      const customerId = changes.customerId.currentValue;
      this.editNotificationForm.get('public_id').setValue(customerId);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getNotificationDetail(publicId) {
    this.dataService.loadSpinner(true);
    let selectorValue = '';
    this.configurationService.getConfiguration(publicId)
      .pipe(
        tap((data: Configuration) => {
          if (data.values.selector == undefined) {
            if (data.values.days >= 0) {
              selectorValue = "2"
            }
            if (data.values.days < 0) {
              selectorValue = "1"
            }
          } else {
            selectorValue = data.values.selector;
          }

          if (data.values.days === 180) {
            this.isLimitInEdit = true;
          } else {
            this.isLimitInEdit = false;
          }

          this.editNotificationForm.patchValue({
            id: data.id,
            name: data.name,
            description: data.description,
            status: data.status,
            object: data.object,
            object_related: data.object_related,
            public_id: data.public_id,
            type: data.type,
            values: {
              days: Math.abs(data.values.days),
              hours: data.values.hours,
              minutes: data.values.minutes,
              template_id: data.values.template_id,
              selector: selectorValue
            }
          });
          this.dataService.loadSpinner(false);
        }),
        catchError(error => {
          console.error('Failed while trying to get Notification Details.' + error);
          this.dataService.loadSpinner(false);
          this.closeEditNotificationSidebar();
          return of(null);
        })
      )
      .subscribe();
  }

  setNotificationStatus(event) {
    const status = this.editNotificationForm.get('status').value;
    this.editNotificationForm.get('status').setValue(status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE');
  }

  observerEditionForm() {
    this.editNotificationForm.get('values.days').valueChanges
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(day => {
        if (day >= 180) {
          this.isLimitInEdit = true;
          this.editNotificationForm.get('values.hours').setValue(0);
          this.editNotificationForm.get('values.minutes').setValue(0);
        } else {
          this.isLimitInEdit = false;
        }
      });
  }

  increment(controlName: string) {
    const control = this.editNotificationForm.get(`values.${controlName}`);
    let value = control.value;

    if (controlName === 'days' && value < 180) {
      value++;
    } else if (controlName === 'hours' && value < 23 && !this.isLimitInEdit) {
      value++;
    } else if (controlName === 'minutes' && value < 59 && !this.isLimitInEdit) {
      value++;
    }

    control.setValue(value);
  }

  decrement(controlName: string) {
    const control = this.editNotificationForm.get(`values.${controlName}`);
    let value = control.value;

    if (value > 0) {
      value--;
    }

    control.setValue(value);
  }

  changeTemplate(event) { }

  initNewNotificationForm() {
    this.editNotificationForm = this.formBuilder.group({
      id: new FormControl(""),
      name: new FormControl("", Validators.required),
      description: new FormControl("", Validators.required),
      status: new FormControl("ACTIVE"),
      object: new FormControl(""),
      object_related: new FormControl("INVOICE"),
      public_id: new FormControl(""),
      type: new FormControl("NOTIFICATION"),
      values: this.formBuilder.group({
        days: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(180)]),
        hours: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(23)]),
        minutes: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(59)]),
        selector: new FormControl("1"),
        template_id: new FormControl("", Validators.required)
      })
    });
    this.observerEditionForm();
  }

  setIcons() {
    return {
      bluePlus: bluePlusIcon,
      mail: mailIcon
    };
  }

  closeEditNotificationSidebar() {
    this.cancelEdition.emit(false);
  }

  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      this.setNotificationStatus(event);
    }
  }

  editNotification() {
    this.dataService.loadSpinner(true);
    const formValue = this.editNotificationForm.value;
    this.configurationService.updateConfiguration(formValue, this.notificationPublicId)
      .pipe(
        takeUntil(this.destroy$),
        tap(data => {
          this.getNotificationList.emit(true);
          this.dataService.loadSpinner(false);
          this.closeEditNotificationSidebar();
        }),
        catchError(error => {
          console.error('Failed while trying to save the Notification Template Details.' + error);
          this.dataService.loadSpinner(false);
          this.closeEditNotificationSidebar();
          return of(null);
        })
      )
      .subscribe();
  }
}
